/**
 * PaperPreview — draws a `PaperGrid` (packages/common/src/printing/preview) as
 * the strip of thermal paper it describes.
 *
 * The grid is already the printer's answer: every run's size, weight, colour
 * and alignment were decided by the same state machine the gateway's own
 * preview runs, and the parity corpus pins the two equal. So this component
 * DECIDES NOTHING about layout — it places each run at the dot offset the grid
 * gives it, at its cell size times its multipliers, on a 203 dpi coordinate
 * system, and scales the whole strip to the space it has.
 *
 * SVG rather than HTML text: the paper is a fixed-pitch grid in printer dots,
 * and SVG is the one place the browser will put a glyph exactly where it is
 * told (`textLength` locks each run to its cells whatever monospace font the
 * platform substitutes). A screen reader gets the printed words, line by line,
 * from the visually hidden transcript — the picture itself is one labelled
 * image.
 *
 * TYPEFACE. `typeface="typographic"` draws the same grid in a real typeface
 * (Inter) — the paper a Western printer
 * set to Typographic receipts prints (design 2026-10-24 §2): the text path's
 * content and layout by construction, only the glyphs change. The caller
 * decides when that applies (receipt-family documents, 58 / 80 mm, Western
 * printers — a Chinese printer's receipts stay text); the grid
 * itself is the same either way.
 */
import * as React from 'react';

import { cn } from '../../utils/utils';
import {
  CELL_HEIGHT_DOTS,
  CELL_WIDTH_DOTS,
  FONT_B_HEIGHT_DOTS,
  FONT_B_WIDTH_DOTS,
  type PaperGrid,
  type PaperRow,
  type PaperRun,
  type PaperCharacterSet,
  linePitchDots,
  paperMargins,
  paperRowText,
  paperRunColumns,
} from '../../printing/preview';

export type PaperTypeface = 'printer' | 'typographic';

const PRINTER_FONT = 'var(--font-mono, ui-monospace, monospace)';
const TYPOGRAPHIC_FONT = 'Inter, var(--font-sans, ui-sans-serif), sans-serif';

/** Paper beyond the print area on each side, in dots. */
const MARGIN_DOTS = 16;
const CUT_DOTS = CELL_HEIGHT_DOTS;
const SYMBOL_DOTS = 144;
const NOTE_DOTS = 22;

interface Placed {
  readonly row: PaperRow;
  readonly y: number;
  readonly height: number;
}

function runCell(run: PaperRun): [number, number] {
  return run.fontB ? [FONT_B_WIDTH_DOTS, FONT_B_HEIGHT_DOTS] : [CELL_WIDTH_DOTS, CELL_HEIGHT_DOTS];
}

/**
 * How far the paper advances for a row. A line advances the printer's line
 * pitch (paperGeometry) or its tallest character, whichever is more; a raster
 * advances its own rows.
 */
function rowHeight(row: PaperRow, pitch: number): number {
  switch (row.t) {
    case 'text':
      return Math.max(pitch, ...row.runs.map((r) => runCell(r)[1] * r.h));
    case 'blank':
      return pitch;
    case 'feed':
      return row.dots;
    case 'cut':
      return CUT_DOTS;
    case 'image':
      return row.bits && row.h ? row.h : SYMBOL_DOTS + 8;
    case 'qr':
      return SYMBOL_DOTS + 8;
    case 'buzzer':
    case 'pulse':
      return NOTE_DOTS;
  }
}

/** What a non-printing event does, in words — shown faintly, never as ink. */
function eventLabel(row: PaperRow): string {
  if (row.t === 'buzzer') return `buzzer · ${row.beeps ?? 1} beep${(row.beeps ?? 1) === 1 ? '' : 's'}`;
  if (row.t === 'pulse') return row.realtime ? 'cash drawer opens' : 'connector pulse (drawer / buzzer)';
  return '';
}

function layout(grid: PaperGrid): { placed: Placed[]; height: number } {
  let y = MARGIN_DOTS;
  const placed: Placed[] = [];
  const pitch = linePitchDots(grid.paperWidthMm);
  for (const row of grid.rows) {
    const height = rowHeight(row, pitch);
    placed.push({ row, y, height });
    y += height;
  }
  return { placed, height: y + MARGIN_DOTS };
}

function Run({
  run,
  x,
  baseline,
  charset,
  typeface,
}: {
  run: PaperRun;
  x: number;
  baseline: number;
  charset: PaperCharacterSet;
  typeface: PaperTypeface;
}) {
  const [cw, ch] = runCell(run);
  // Columns, not characters: a Chinese printer's hanzi is two cells wide.
  const width = paperRunColumns(run, charset) * cw;
  // A monospace advance is ~0.6em, so this font size fills one cell.
  const fontSize = cw / 0.6;
  const ink = run.red ? 'var(--color-paper-alert)' : 'var(--color-paper-ink)';
  return (
    <g transform={`translate(${x} ${baseline}) scale(${run.w} ${run.h})`}>
      {run.inv ? <rect x={0} y={-ch} width={width} height={ch} fill={ink} /> : null}
      <text
        x={0}
        y={-Math.round(ch * 0.2)}
        fontSize={fontSize}
        fontWeight={run.bold ? 700 : 400}
        fontFamily={typeface === 'typographic' ? TYPOGRAPHIC_FONT : PRINTER_FONT}
        fill={run.inv ? 'var(--color-paper)' : ink}
        textLength={width}
        lengthAdjust="spacingAndGlyphs"
        style={{ whiteSpace: 'pre' }}
      >
        {run.s}
      </text>
      {run.ul ? <rect x={0} y={-2} width={width} height={1.5} fill={ink} /> : null}
    </g>
  );
}

/**
 * A raster exactly as burned: one SVG rect per horizontal run of ink dots
 * (far fewer elements than one per dot, and no image decode in the browser).
 */
function RasterImage({ x, y, w, h, bits }: { x: number; y: number; w: number; h: number; bits: string }) {
  const d = React.useMemo(() => {
    const stride = w / 8;
    let path = '';
    for (let row = 0; row < h; row += 1) {
      let start = -1;
      for (let col = 0; col <= w; col += 1) {
        let on = false;
        if (col < w) {
          const byte = parseInt(bits.substr((row * stride + (col >> 3)) * 2, 2), 16);
          on = (byte & (0x80 >> (col & 7))) !== 0;
        }
        if (on && start < 0) start = col;
        if (!on && start >= 0) {
          path += `M${x + start} ${y + row}h${col - start}v1h${start - col}z`;
          start = -1;
        }
      }
    }
    return path;
  }, [x, y, w, h, bits]);
  return <path data-row="image" d={d} fill="var(--color-paper-ink)" shapeRendering="crispEdges" />;
}

function SymbolBox({
  kind,
  x: left,
  y,
  dots,
  align,
}: {
  kind: 'qr' | 'image';
  x: number;
  y: number;
  dots: number;
  align: 'left' | 'center' | 'right';
}) {
  const x = left + (align === 'center' ? (dots - SYMBOL_DOTS) / 2 : align === 'right' ? dots - SYMBOL_DOTS : 0);
  return (
    <g data-row={kind}>
      <rect
        x={x}
        y={y + 4}
        width={SYMBOL_DOTS}
        height={SYMBOL_DOTS}
        fill="none"
        stroke="var(--color-paper-ink)"
        strokeWidth={2}
        strokeDasharray={kind === 'image' ? '6 4' : undefined}
      />
      <text x={x + SYMBOL_DOTS / 2} y={y + 4 + SYMBOL_DOTS / 2 + 6} textAnchor="middle" fontSize={18} fill="var(--color-paper-ink)">
        {kind === 'qr' ? 'QR code' : 'Logo'}
      </text>
    </g>
  );
}

/** A bracket over blank paper, labelled with its length — drawn on the preview, never on the paper. */
function MarginBand({
  y,
  height,
  width,
  label,
  testId,
}: {
  y: number;
  height: number;
  width: number;
  label: string;
  testId: string;
}) {
  return (
    <g data-testid={testId} aria-hidden="true">
      <rect x={0} y={y} width={width} height={height} fill="var(--color-muted-foreground)" opacity={0.08} />
      <line x1={6} x2={6} y1={y + 2} y2={y + height - 2} stroke="var(--color-muted-foreground)" strokeWidth={2} />
      <text x={14} y={y + Math.min(height / 2 + 5, height - 4)} fontSize={15} fill="var(--color-muted-foreground)">
        {label}
      </text>
    </g>
  );
}

function Row({
  placed,
  dots,
  charset,
  typeface,
}: {
  placed: Placed;
  dots: number;
  charset: PaperCharacterSet;
  typeface: PaperTypeface;
}) {
  const { row, y, height } = placed;
  const left = MARGIN_DOTS;
  switch (row.t) {
    case 'text': {
      let x = left + (row.x ?? 0);
      return (
        <g data-row="text">
          {row.runs.map((run, i) => {
            const el = <Run key={i} run={run} x={x} baseline={y + height} charset={charset} typeface={typeface} />;
            x += paperRunColumns(run, charset) * runCell(run)[0] * run.w;
            return el;
          })}
        </g>
      );
    }
    case 'cut':
      return (
        <g data-row="cut">
          <line
            x1={0}
            x2={dots + 2 * MARGIN_DOTS}
            y1={y + height / 2}
            y2={y + height / 2}
            stroke="var(--color-muted-foreground)"
            strokeWidth={1.5}
            strokeDasharray="8 6"
          />
          <text
            x={dots + 2 * MARGIN_DOTS - 4}
            y={y + height / 2 - 4}
            textAnchor="end"
            fontSize={12}
            fill="var(--color-muted-foreground)"
          >
            cut
          </text>
        </g>
      );
    case 'image':
      if (row.bits && row.w && row.h) return <RasterImage x={left} y={y} w={row.w} h={row.h} bits={row.bits} />;
      return <SymbolBox kind="image" x={left} y={y} dots={dots} align={row.align} />;
    case 'qr':
      return <SymbolBox kind="qr" x={left} y={y} dots={dots} align={row.align} />;
    case 'buzzer':
    case 'pulse':
      return (
        <text
          data-row={row.t}
          x={left + dots / 2}
          y={y + height - 6}
          textAnchor="middle"
          fontSize={13}
          fontStyle="italic"
          fill="var(--color-muted-foreground)"
        >
          {`( ${eventLabel(row)} )`}
        </text>
      );
    default:
      return null;
  }
}

export interface PaperPreviewProps {
  readonly grid: PaperGrid;
  /**
   * Mark the blank paper: the top margin (the part torn against the bar) and
   * the feed before each cut, with their line counts and length in mm.
   */
  readonly showMargins?: boolean;
  /** What this paper is, for assistive technology ("Kitchen ticket at 80 mm"). */
  readonly label: string;
  readonly className?: string;
  /**
   * `fit` (default) fills its container's width; a number draws the paper at
   * that many CSS pixels per printer dot (1 = 100 %), so a long or wide
   * ticket can be inspected — the caller supplies the scrolling.
   */
  readonly zoom?: 'fit' | number;
  /**
   * `printer` (default): the printer's own character cells. `typographic`:
   * the same grid drawn in a real typeface — a receipt on a printer set to
   * Typographic receipts.
   */
  readonly typeface?: PaperTypeface;
}

/** The paper's full drawn width in dots, margins included. */
export function paperPreviewWidthDots(grid: PaperGrid): number {
  return grid.dots + 2 * MARGIN_DOTS;
}

/** The paper, drawn. Width follows its container (or the zoom); the aspect ratio is the paper's own. */
export function PaperPreview({
  grid,
  label,
  className,
  showMargins = false,
  zoom = 'fit',
  typeface = 'printer',
}: PaperPreviewProps) {
  const charset: PaperCharacterSet = grid.characterSet ?? 'Western';
  const { placed, height } = React.useMemo(() => layout(grid), [grid]);
  const margins = React.useMemo(() => (showMargins ? paperMargins(grid) : null), [grid, showMargins]);
  const span = (start: number, lines: number) => {
    const first = placed[start];
    const last = placed[start + lines - 1];
    return { y: first.y, height: last.y + last.height - first.y };
  };
  const mmLabel = (lines: number, mm: number) => `${lines} line${lines === 1 ? '' : 's'} ≈ ${Math.round(mm)} mm`;
  const width = grid.dots + 2 * MARGIN_DOTS;
  const transcript = React.useMemo(
    () =>
      grid.rows
        .map((row) => (row.t === 'text' ? paperRowText(row).trimEnd() : row.t === 'blank' || row.t === 'feed' ? '' : `[${row.t}]`))
        .join('\n'),
    [grid],
  );
  return (
    <figure
      className={cn('m-0', zoom === 'fit' ? 'w-full' : 'shrink-0', className)}
      style={zoom === 'fit' ? undefined : { width: `${Math.round(width * zoom)}px` }}
      data-testid="paper-preview"
      data-columns={grid.columns}
      data-height-dots={Math.round(height)}
      data-typeface={typeface}
      data-character-set={charset}
    >
      <svg
        role="img"
        aria-label={label}
        viewBox={`0 0 ${width} ${height}`}
        className="block h-auto w-full rounded-sm bg-paper shadow-md ring-1 ring-border"
        preserveAspectRatio="xMidYMin meet"
      >
        {placed.map((p, i) => (
          <Row key={i} placed={p} dots={grid.dots} charset={charset} typeface={typeface} />
        ))}
        {margins?.top ? (
          <MarginBand
            testId="paper-margin-top"
            {...span(margins.top.startRow, margins.top.lines)}
            width={width}
            label={`Top margin · ${mmLabel(margins.top.lines, margins.top.mm)} (torn off)`}
          />
        ) : null}
        {margins?.beforeCut.map((m) => (
          <MarginBand
            key={m.startRow}
            testId="paper-margin-cut"
            {...span(m.startRow, m.lines)}
            width={width}
            label={`Feed before cut · ${mmLabel(m.lines, m.mm)} + cutter`}
          />
        ))}
      </svg>
      {margins ? (
        <p className="sr-only" data-testid="paper-margins">
          {[
            margins.top ? `Top margin ${margins.top.lines} lines, about ${Math.round(margins.top.mm)} mm.` : 'No top margin.',
            ...margins.beforeCut.map((m) => `Feed before cut ${m.lines} lines, about ${Math.round(m.mm)} mm, plus the cutter's own feed.`),
          ].join(' ')}
        </p>
      ) : null}
      <figcaption className="sr-only">
        <pre data-testid="paper-preview-text">{transcript}</pre>
      </figcaption>
    </figure>
  );
}
